import { create } from 'zustand';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export const useAuthStore = create((set, get) => ({
  user: null,
  loading: false,
  error: null,

  // Helper method to get connected account by provider
  getConnectedAccount: (provider) => {
    const { user } = get();
    return user?.connectedAccounts?.find(acc => acc.provider === provider) || null;
  },

  // Helper method to check if provider is connected
  isProviderConnected: (provider) => {
    const { user } = get();
    return user?.connectedAccounts?.some(acc => acc.provider === provider) || false;
  },

  // Helper methods for backward compatibility
  getFlickrAccount: () => {
    const { getConnectedAccount } = get();
    return getConnectedAccount('flickr');
  },

  // Check authentication status
  checkAuth: async () => {
    console.log('🔐 AuthStore: Starting authentication check...');
    console.log('🔐 AuthStore: API_BASE:', API_BASE);
    console.log('🔐 AuthStore: Environment check:', {
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      NODE_ENV: process.env.NODE_ENV
    });
    
    set({ loading: true, error: null });
    
    try {
      console.log('🔐 AuthStore: Checking auth at:', `${API_BASE}/api/auth/me`);
      const response = await axios.get(`${API_BASE}/api/auth/me`, {
        withCredentials: true
      });
      
      console.log('✅ AuthStore: Auth check successful:', response.data);
      set({ user: response.data, loading: false });
      return response.data;
    } catch (error) {
      console.log('❌ AuthStore: Auth check failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      set({ user: null, loading: false });
      return null;
    }
  },

  // Initiate OAuth for any provider
  initiateAuth: async (provider = 'flickr') => {
    set({ loading: true, error: null });
    
    try {
      const response = await axios.get(`${API_BASE}/api/auth/${provider}`, {
        withCredentials: true
      });
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error(`${provider} auth initiation failed:`, error);
      set({ 
        loading: false, 
        error: error.response?.data?.error || `Failed to initiate ${provider} authentication` 
      });
    }
  },

  // Legacy method for backward compatibility
  initiateFlickrAuth: async () => {
    return get().initiateAuth('flickr');
  },

  // Disconnect a provider account
  disconnectProvider: async (provider) => {
    set({ loading: true, error: null });
    
    try {
      const response = await axios.delete(`${API_BASE}/api/auth/disconnect/${provider}`, {
        withCredentials: true
      });
      
      // Update user in store with new connected accounts
      const { user } = get();
      if (user) {
        set({ 
          user: { 
            ...user, 
            connectedAccounts: response.data.connectedAccounts 
          }, 
          loading: false 
        });
      }
      
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error(`Disconnect ${provider} error:`, error);
      const errorMessage = error.response?.data?.error || `Failed to disconnect ${provider} account`;
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  // Logout
  logout: async () => {
    set({ loading: true });

    try {
      await axios.post(`${API_BASE}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      set({ user: null, loading: false, error: null });
    }
  },

  // Register an initial password for the current account
  registerPassword: async ({ email, password, verificationToken }) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/password/register`, {
        email,
        password,
        verificationToken
      }, {
        withCredentials: true
      });

      await get().checkAuth();
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to set password';
      throw new Error(message);
    }
  },

  // Log in with email and password
  loginWithPassword: async ({ email, password }) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/password/login`, {
        email,
        password
      }, {
        withCredentials: true
      });

      await get().checkAuth();
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to log in';
      throw new Error(message);
    }
  },

  // Update user preferences
  updatePreferences: async (preferences) => {
    const { user } = get();
    if (!user) return;

    try {
      const response = await axios.put(`${API_BASE}/api/auth/preferences`, preferences, {
        withCredentials: true
      });
      
      set({ 
        user: { ...user, preferences: response.data.preferences }
      });
      
      return response.data;
    } catch (error) {
      set({ 
        error: error.response?.data?.error || 'Failed to update preferences' 
      });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Set user (for testing or manual updates)
  setUser: (user) => set({ user }),
}));
